import {
  LoginResponseDto,
  AuthenticatedAccountDto,
  UpdateAccountRequestDto,
  MeResponseDto,
} from '@ivy-monorepo/shared'
import { apiClient } from './api-client'
import { LocalStorageKey } from '../helpers/constants'
import { getCurrentRoleRoute } from '../helpers/auth'

class AuthService {
  private authCheckInterval: NodeJS.Timeout | null = null
  private subscribers: ((isAuthenticated: boolean) => void)[] = []
  private readonly REFRESH_THRESHOLD = 30 // seconds

  constructor() {
    this.scheduleTokenRefresh()
  }

  private scheduleTokenRefresh() {
    if (this.authCheckInterval) {
      clearTimeout(this.authCheckInterval)
    }

    const remaining = this.getAuthRemaining() / 1000

    if (remaining <= 0) {
      this.notifySubscribers(false)
      return
    }

    // Schedule refresh 1 minute before expiration
    const refreshTime = Math.max(0, remaining - this.REFRESH_THRESHOLD) * 1000

    this.authCheckInterval = setTimeout(async () => {
      try {
        await this.refreshToken()
        this.scheduleTokenRefresh() // Reschedule after successful refresh
      } catch (error) {
        console.error('Token refresh failed:', error)
        this.notifySubscribers(false)
      }
    }, refreshTime)
  }

  private notifySubscribers(isAuthenticated: boolean) {
    this.subscribers.forEach(callback => callback(isAuthenticated))
  }

  subscribe(callback: (isAuthenticated: boolean) => void) {
    this.subscribers.push(callback)
    return () => {
      this.subscribers = this.subscribers.filter(cb => cb !== callback)
    }
  }

  getAuthRemaining(): number {
    const authDataFromStorage = localStorage.getItem(LocalStorageKey.auth)
    if (!authDataFromStorage) return 0

    const authData = JSON.parse(authDataFromStorage) as LoginResponseDto
    if (!authData || !authData.expiresAt) return 0

    return Math.max(0, new Date(authData.expiresAt).getTime() - new Date().getTime())
  }

  getAuthRemainingPercentage(): number {
    const authDataFromStorage = localStorage.getItem(LocalStorageKey.auth)
    if (!authDataFromStorage) return 0

    const authData = JSON.parse(authDataFromStorage) as LoginResponseDto
    if (!authData || !authData.expiresAt) return 0

    const totalTime = 600 * 1000 // 10 minutes in milliseconds
    const remaining = new Date(authData.expiresAt).getTime() - new Date().getTime()

    return Math.max(0, Math.round((1000.0 * remaining) / totalTime)) / 10.0
  }

  isAuthenticated(): boolean {
    return this.getAuthRemaining() > 0
  }

  async login(email: string, password: string): Promise<LoginResponseDto> {
    email = email.toLowerCase()

    try {
      const response = await apiClient.post('/auth/login', { email, password })
      const loginResponse = response.data as LoginResponseDto

      localStorage.setItem(LocalStorageKey.auth, JSON.stringify(loginResponse))

      this.scheduleTokenRefresh()
      this.notifySubscribers(true)
      return loginResponse
    } catch (error: any) {
      throw this.mapLoginError(error)
    }
  }

  async loginWithGoogle(idToken: string): Promise<LoginResponseDto> {
    try {
      const response = await apiClient.post('/auth/google/admin', { idToken })
      const loginResponse = response.data as LoginResponseDto

      localStorage.setItem(LocalStorageKey.auth, JSON.stringify(loginResponse))

      this.scheduleTokenRefresh()
      this.notifySubscribers(true)
      return loginResponse
    } catch (error: any) {
      throw this.mapLoginError(error)
    }
  }

  private mapLoginError(error: any): Error {
      if (error.name === 'AxiosError' || error.isAxiosError) {
        const apiError = error.response?.data
        if (apiError?.code || apiError?.errorCode) {
          return this.mapLoginErrorByCode(
            apiError.code || apiError.errorCode,
            apiError.displayMessage || apiError.message,
          )
        }
        return new Error(
          `The request could not be completed, the backend API may not be configured correctly.`,
        )
      }

      if (error.code) {
        return this.mapLoginErrorByCode(error.code, error.message)
      }

      return new Error('Login failed')
  }

  private mapLoginErrorByCode(code: string, fallbackMessage?: string): Error {
        switch (code) {
          case 'AccountCredentialsInvalid':
            return new Error(
              `The credentials you provided are invalid. Please check your email address and password and try again to log in.`
            )

          case 'AccountDoesNotExist':
            return new Error(
              `No account exists for this Google email. An administrator must create your account before you can sign in with Google.`
            )

          case 'AccountExternalCredentialsInvalid':
            return new Error(
              `The Google account does not appear to be configured as an account on this system. Please try signing in with an email address and password, or create a new account on the registration page.`
            )

          case 'AccountEmailNotConfirmed':
            return new Error(
              `You cannot log in until you have confirmed your account. Please check your email for the welcome message we sent when you registered.<br/><br/>If you have not received the welcome message, please use the link below to request a password reset.`
            )

          case 'AccountLockedOut':
          case 'AccountLockedOutOverride':
            return new Error(
              `Your account has been locked due to too many failed sign-in attempts. Please wait for 30 minutes, then request a password reset.`
            )

          case 'AccountTombstoned':
            return new Error(`Your account has been disabled.`)

          case 'AccountCredentialsExpired':
          case 'AccountCredentialsNotConfirmed':
            return new Error(
              `Your account credentials are expired. Please check your email for a password reset email, or click the Forgot Password link below to retry.`
            )

          case 'AccountRequiresIdentityProviderLocal':
            return new Error(
              `You created your account using an email address and password. Please log in using these credentials rather than using the Google sign-in framework.`
            )

          case 'AccountRequiresIdentityProviderGoogle':
            return new Error(
              `You created your account using your Google account, rather than using a standard email address and password. Please log in using the Google sign-in button.`
            )

          case 'AccountRequiresIdentityProviderApple':
            return new Error(
              `You created your account using your Apple account, rather than using a standard email address and password. Please log in using Apple Sign-In.`
            )

          case 'AccountRoleInvalidForOperation':
            return new Error(
              `Google sign-in is not available for this account type. Please use email and password, or the mobile app.`
            )

          case 'GoogleJwtBearerTokenInvalid':
            return new Error(
              `Your account could not be validated by Google. Please check that your Google account is valid and try again.`
            )

          case 'GoogleJwtNonceInvalid':
            return new Error(
              `Your account could not be validated by Google, the nonce is invalid. Please check that your Google account is valid and try again.`
            )

          case 'AppleJwtBearerTokenInvalid':
            return new Error(
              `Your account could not be validated by Apple. Please check that your Apple account is valid and try again.`
            )

          case 'AppleJwtNonceInvalid':
            return new Error(
              `Your account could not be validated by Apple, the nonce is invalid. Please check that your Apple account is valid and try again.`
            )

          default:
            return new Error(fallbackMessage || code)
        }
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout')
    } finally {
      localStorage.removeItem(LocalStorageKey.auth)
      if (this.authCheckInterval) {
        clearTimeout(this.authCheckInterval)
        this.authCheckInterval = null
      }
      this.notifySubscribers(false)
    }
  }

  async requestPasswordReset(email: string): Promise<void> {
    // Convert email to lowercase
    email = email.toLowerCase()

    try {
      await apiClient.post('/auth/password/reset', { email })
    } catch (error: any) {
      if (error.response && error.response.data) {
        throw new Error(error.response.data.message || 'Failed to request password reset')
      }
      throw new Error('Failed to request password reset')
    }
  }

  async updatePassword(token: string, email: string, newPassword: string): Promise<void> {
    // Convert email to lowercase
    email = email.toLowerCase()
    try {
      await apiClient.post(
        '/auth/password',
        { token, email, newPassword },
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      )
    } catch (error: any) {
      console.log('Error during password update:', error)
      // Preserve the error structure from the API client
      throw error
    }
  }

  async getMe(): Promise<MeResponseDto | null> {
    if (!this.isAuthenticated()) {
      return null
    }

    try {
      const response = await apiClient.get<MeResponseDto>(`/${getCurrentRoleRoute()}/me`)
      return response.data
    } catch (error) {
      console.error('Failed to get current account:', error)
      return null
    }
  }

  async getCurrentAccount(): Promise<LoginResponseDto['account'] | null> {
    const me = await this.getMe()
    return me?.account ?? null
  }

  async refreshToken(): Promise<LoginResponseDto> {
    try {
      let response
      try {
        response = await apiClient.post(
          '/auth/refresh',
          {},
          {
            headers: {
              'Content-Type': 'application/json',
            },
            timeout: 1000, // 5 second timeout
          }
        )
      } catch (requestError: any) {
        console.error('Request failed:', {
          message: requestError.message,
          status: requestError.response?.status,
          data: requestError.response?.data,
          headers: requestError.response?.headers,
          config: requestError.config,
          code: requestError.code,
          name: requestError.name,
        })
        throw requestError
      }

      // Ensure response.data exists and has the required properties
      if (!response.data) {
        throw new Error('No data received in refresh response')
      }

      const { token, grantedAt, expiresAt, account } = response.data
      if (!token || !grantedAt || !expiresAt || !account) {
        console.error('Invalid refresh response structure:', response.data)
        throw new Error('Invalid refresh response structure')
      }

      const refreshResponse: LoginResponseDto = {
        token,
        grantedAt,
        expiresAt,
        account,
      }

      localStorage.setItem(LocalStorageKey.auth, JSON.stringify(refreshResponse))

      // Schedule the next refresh after successful refresh
      this.scheduleTokenRefresh()

      return refreshResponse
    } catch (error: any) {
      console.error('Token refresh failed:', error)
      console.error('Error details:', {
        message: error.message,
        status: error.response?.status,
        data: error.response?.data,
        headers: error.response?.headers,
      })
      await this.logout()
      throw error
    }
  }

  async updateAccount(
    updateData: UpdateAccountRequestDto,
    avatarFile?: File
  ): Promise<AuthenticatedAccountDto> {
    // If we have a file, use FormData
    if (avatarFile) {
      const formData = new FormData()

      // Add text fields
      if (updateData.firstName) formData.append('firstName', updateData.firstName)
      if (updateData.lastName) formData.append('lastName', updateData.lastName)
      // Add the file
      formData.append('avatar', avatarFile)

      const response = await apiClient.patch(`/${getCurrentRoleRoute()}/me`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      })

      return Object.assign(new AuthenticatedAccountDto(), response.data.account)
    }

    // Otherwise use JSON
    const response = await apiClient.patch(`/${getCurrentRoleRoute()}/me`, updateData)

    return Object.assign(new AuthenticatedAccountDto(), response.data)
  }
}

export const authService = new AuthService()
